import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2 } from "lucide-react";
import { Reveal, MaskedReveal } from "../components/Reveal";
import { SERVICES, IMAGES } from "../lib/site";

const PageHeader = () => (
  <section className="relative bg-navy text-white overflow-hidden grain" data-testid="services-header">
    <div className="absolute inset-0 hero-grid" />
    <div className="absolute -top-32 left-1/3 w-96 h-96 rounded-full bg-gold/15 blur-[120px]" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <p className="text-xs uppercase tracking-[0.3em] font-bold text-gold">Our Services</p>
      <h1 className="mt-4 font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl" data-testid="services-heading">
        <MaskedReveal delay={0.1}>Full-Stack IT Services,</MaskedReveal>
        <MaskedReveal delay={0.22}>
          <span className="text-gold">End to End.</span>
        </MaskedReveal>
      </h1>
      <p className="mt-6 max-w-2xl text-slate-400 text-base sm:text-lg leading-relaxed">
        Strategy, design, engineering, deployment and support — all under one roof. Choose a service and send
        us an enquiry.
      </p>
    </div>
  </section>
);

const ServiceCards = () => (
  <section className="bg-ivory py-24" data-testid="services-list">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {SERVICES.map((s, i) => (
        <Reveal key={s.id} delay={(i % 3) * 0.08}>
          <div className="group bg-white rounded-3xl border border-slate-200 p-8 h-full flex flex-col hover:border-royal/50 hover:shadow-2xl hover:shadow-royal/10 hover:-translate-y-1.5 transition-all duration-300" data-testid={`service-card-${s.id}`}>
            <div className="w-14 h-14 rounded-2xl bg-navy flex items-center justify-center group-hover:bg-royal group-hover:rotate-3 transition-all duration-300">
              <s.icon className="text-gold" size={26} strokeWidth={2} />
            </div>
            <h2 className="mt-6 font-display text-2xl font-extrabold text-ink">{s.title}</h2>
            <p className="mt-3 text-sm text-slate-500 leading-relaxed">{s.short}</p>
            <ul className="mt-5 space-y-2.5 flex-1">
              {s.points.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              state={{ service: s.title }}
              data-testid={`service-enquire-${s.id}`}
              className="mt-7 inline-flex items-center justify-center gap-2 bg-navy text-white text-sm font-bold py-3 rounded-full hover:bg-royal transition-colors duration-300"
            >
              Enquire Now <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Methodology = () => (
  <section className="bg-white py-24 border-y border-slate-200" data-testid="methodology-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-royal text-center">Delivery Methodology</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black text-ink tracking-tight text-center">
          From Brief to Launch in 4 Steps
        </h2>
      </Reveal>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { n: "01", t: "Discovery", d: "Free consultation call. We understand your business, goals and constraints before writing a single line of code." },
          { n: "02", t: "Proposal & SLA", d: "Written scope, fixed milestones, transparent pricing and a signed service-level agreement." },
          { n: "03", t: "Build & Review", d: "Agile sprints with weekly demos. You see progress every week — not just at the end." },
          { n: "04", t: "Launch & Support", d: "Deployment, monitoring and 24×7 support. We stay accountable after go-live." },
        ].map((s, i) => (
          <Reveal key={s.n} delay={i * 0.1}>
            <div className="relative bg-ivory rounded-3xl border border-slate-200 p-7 h-full" data-testid={`methodology-step-${i}`}>
              <p className="font-display text-5xl font-black text-stroke-navy">{s.n}</p>
              <h3 className="mt-4 font-display text-xl font-bold text-ink">{s.t}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.d}</p>
              {i < 3 && <ArrowUpRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 text-gold z-10" size={22} />}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TechStack = () => (
  <section className="bg-navy py-24 relative overflow-hidden grain" data-testid="tech-stack-section">
    <div className="absolute inset-0 hero-grid" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-gold">Technology</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black text-white tracking-tight">
            Stacks We Ship On
          </h2>
          <p className="mt-5 text-slate-400 leading-relaxed">
            Modern, battle-tested technologies — chosen for reliability, not hype.
          </p>
        </Reveal>
        <div className="mt-8 flex flex-wrap gap-3" data-testid="tech-chips">
          {["React", "Next.js", "Node.js", "Python", "Flutter", "AWS", "Azure", "Docker", "Kubernetes", "PostgreSQL", "MongoDB", "GraphQL", "WhatsApp Business API", "Firebase"].map((t, i) => (
            <Reveal key={t} delay={i * 0.04}>
              <span className="inline-block bg-white/5 border border-white/15 text-slate-200 text-sm font-semibold px-5 py-2.5 rounded-full hover:border-gold/60 hover:text-gold-light transition-colors">
                {t}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.15}>
        <div className="relative rounded-[1.75rem] overflow-hidden border border-white/15 shadow-2xl">
          <img src={IMAGES.developer} alt="Engineering at RICHIE RICH" className="w-full h-[24rem] object-cover" data-testid="tech-stack-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Services = () => (
  <>
    <PageHeader />
    <ServiceCards />
    <Methodology />
    <TechStack />
  </>
);

export default Services;
